import { ToggleTextButton } from "hec/components/cta";
import { conditionOptionsMap } from "hec/components/cta/toggle-text-button/condition-options";
import { getDetermineRouteTypeAndUrl } from "hec/hooks/useDetermineRouteTypeAndUrl";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import { isEditing } from "@builder.io/sdk-react";

import { type SiteId } from "~/hec/utils/site-id";
import { useRootLayoutData } from "~/root";

import type { ModelReference } from "../BuilderReferenceContent";
import HeroToggleTextButton from "./HeroToggleTextButton";
import { buttonFunctions, coreButtonInputs } from "./cta.helper";
import useFetchPageDataForCta from "./useFetchPageDataForCta";

interface QueryParam {
  name: string;
  value: string;
}

interface BuilderToggleTextButtonProps {
  condition: string;
  conditionMetText: string;
  isLearningCenterHero?: string | boolean;
  heroRole?: "take_quiz" | "continue";
  conditionNotMetText: string;
  stateToShowInEditor: string;
  disabled?: boolean;
  variant?: string;
  openInNewTab?: boolean;
  clickFunction?: string;
  scrollTargetId?: string;
  isInternalLink?: boolean;
  isRevShare?: boolean;
  pageReference?: ModelReference;
  queryParams?: QueryParam[];
  externalLinkReference?: ModelReference;
  attributes?: {
    className?: string;
    [key: string]: any;
  };
  "data-testid"?: string;
}

const BuilderToggleTextButton = ({
  condition,
  conditionMetText,
  isLearningCenterHero,
  heroRole,
  conditionNotMetText,
  stateToShowInEditor,
  disabled,
  variant,
  openInNewTab,
  clickFunction = buttonFunctions.linkToPage,
  scrollTargetId,
  isInternalLink,
  isRevShare,
  pageReference,
  queryParams,
  externalLinkReference,
  attributes,
  "data-testid": dataTestId,
}: BuilderToggleTextButtonProps) => {
  const { className: attrClassName, ...restAttributes } = attributes || {};
  const isEdit = isEditing();
  const isHero =
    isLearningCenterHero === true || isLearningCenterHero === "true";

  const {
    pageRef,
    externalLinkRef,
    isResolved,
    isLoading: isPageDataLoading,
    error: pageDataError,
  } = useFetchPageDataForCta({
    isInternalLink,
    pageReference,
    externalLinkReference,
  });

  const { site } = useRootLayoutData();
  const { routeType, url } = getDetermineRouteTypeAndUrl({
    clickFunction,
    isInternalLink,
    // No Suspense boundary here either — see the twin comment in `button.tsx`.
    // An unresolved reference used to arrive as a pending promise and yield a
    // url-less CTA; the guard preserves that output without passing a promise
    // into a function typed for plain data.
    pageReference: isResolved ? pageRef : undefined,
    externalLinkReference: isResolved ? externalLinkRef : undefined,
    siteId: site.id as SiteId,
  });

  if (isPageDataLoading) {
    return null;
  }

  if (pageDataError) {
    console.error("Error fetching page data for CTA: ", pageDataError);
    return (
      <ToggleTextButton
        ctaType="button"
        condition={condition}
        conditionMetText={conditionMetText}
        conditionNotMetText={conditionNotMetText}
        stateToShowInEditor={stateToShowInEditor}
        disabled={true}
        variant={variant as any}
        clickFunction={clickFunction}
        isEdit={isEdit}
        className={attrClassName}
        isRevShare={isRevShare}
        {...restAttributes}
        data-testid={dataTestId || "toggleTextButton-ToggleTextButton-error"}
      />
    );
  }

  const HeroToggleTextBtnProps = {
    condition,
    conditionMetText,
    conditionNotMetText,
    stateToShowInEditor,
    clickFunction,
    attributes,
    heroRole,
    isRevShare,
    "data-testid": dataTestId,
    scrollTargetId,
  };

  return isHero ? (
    <HeroToggleTextButton {...HeroToggleTextBtnProps} />
  ) : (
    <ToggleTextButton
      ctaType="button"
      condition={condition}
      conditionMetText={conditionMetText}
      conditionNotMetText={conditionNotMetText}
      stateToShowInEditor={stateToShowInEditor}
      disabled={disabled}
      variant={variant as any}
      openInNewTab={openInNewTab}
      clickFunction={clickFunction}
      scrollTargetId={scrollTargetId}
      queryParams={queryParams}
      routeType={routeType ?? undefined}
      url={url ?? undefined}
      isEdit={isEdit}
      className={attrClassName}
      isRevShare={isRevShare}
      {...restAttributes}
      data-testid={dataTestId || "toggleTextButton-ToggleTextButton"}
    />
  );
};

export const ToggleTextButtonDefinition = {
  component: BuilderToggleTextButton,
  name: "ToggleTextButton",
  image: "https://unpkg.com/css.gg@2.0.0/icons/svg/link.svg",
  noWrap: true,
  inputs: [
    {
      name: "condition",
      type: "string",
      enum: Object.values(conditionOptionsMap),
      required: true,
    },
    {
      name: "conditionMetText",
      type: "string",
      required: true,
    },
    {
      name: "conditionNotMetText",
      type: "string",
      required: true,
    },
    {
      name: "stateToShowInEditor",
      type: "string",
      enum: ["Condition Met", "Condition Not Met"],
      defaultValue: "Condition Met",
    },
    {
      name: "variant",
      type: "string",
      enum: ["primary", "secondary"],
      defaultValue: "primary",
    },
    {
      name: "isLearningCenterHero",
      type: "string",
      enum: ["true", "false"],
      defaultValue: "false",
    },
    {
      name: "heroRole",
      type: "string",
      enum: ["take_quiz", "continue"],
      helperText:
        "Declare which hero action this instance represents so the component can auto-highlight the correct CTA and wire Continue to last-visited.",
      defaultValue: "take_quiz",
    },
    {
      name: "disabled",
      type: "boolean",
      defaultValue: false,
    },
    ...coreButtonInputs,
  ],
} satisfies RegisteredComponent;

export default BuilderToggleTextButton;
